import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { MasterMessage } from '../../../../masters/masterMessages';
import Swal from 'sweetalert2';
import ListView from '../../../components/views/listView';
import ModalLoading from '../../../components/general/loading/modalLoading';
import DropZoneField from '../../../components/general/fields/dropzoneField';
import Grid from '@mui/material/Grid';
import { Box, Button, Card, CardContent, LinearProgress, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { SwalCustomClass } from '../../../components/general/toastSwal/swalCustomClass';
import {
    asyncGetTemplates,
    asyncPostLoadBaseGupshup,
} from '../../../../services/commercialCrm.service';
import AutocompleteField from '../../../components/general/fields/autoCompleteField';
import { CSVRender } from '../../../components/csvViewer/csvViewer';
import { requiredFieldsCustom } from './fields';
import GupshupCsvHelpAccordionCustom from './gupshupCsvHelpAccordionCustom';
import { Stack } from '@mui/material';
import { asyncPostFilesBulk, chunkArray, BulkUploadResult } from '../../../../services/files.service';

const LoadBaseGupshupCustom = () => {
    const {
        titleListWhatsappCustom,
        messageError,
        titleModalLoadingWhatsappCustom,
        messageModalLoading,
    } = MasterMessage.commercialCrm.loadBase;
    const [loading, setLoading] = useState(false);
    const [files, setFiles] = useState<any>([]);
    const [templates, setTemplates] = useState<any[]>();
    const [template, setTemplate] = useState<string>();
    const [showCsvViewer, setShowCsvViewer] = useState(false);

    // Estados para carga de carpeta (S3)
    const [bulkFiles, setBulkFiles] = useState<File[]>([]);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [results, setResults] = useState<BulkUploadResult[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.setAttribute('webkitdirectory', '');
            inputRef.current.setAttribute('directory', '');
        }
    }, []);

    const handlePickFolder = () => {
        inputRef.current?.click();
    };

    const onFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
        const list = event.target.files;
        if (!list) return;
        const newFiles: File[] = [];
        for (let i = 0; i < list.length; i++) {
            const f = list.item(i);
            if (f) newFiles.push(f);
        }
        setBulkFiles(newFiles);
    };

    const onDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const dt = event.dataTransfer;
        const droppedFiles = Array.from(dt.files || []);
        if (droppedFiles.length) {
            setBulkFiles(droppedFiles);
            return;
        }
    }, []);

    const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    const totalSizeMB = useMemo(() => {
        const totalBytes = bulkFiles.reduce((acc, f) => acc + f.size, 0);
        return (totalBytes / (1024 * 1024)).toFixed(2);
    }, [bulkFiles]);

    const startUpload = async () => {
        if (!bulkFiles.length || uploading) return;
        setUploading(true);
        setProgress(0);
        setResults([]);

        try {
            const CHUNK_SIZE = 100;
            const chunks = chunkArray(bulkFiles, CHUNK_SIZE);
            for (let i = 0; i < chunks.length; i++) {
                const items = chunks[i];
                const response = await asyncPostFilesBulk(items);
                const respItems = Array.isArray(response?.items)
                    ? response.items
                    : Array.isArray((response as any)?.items?.items)
                    ? (response as any).items.items
                    : [];
                setResults((prev) => [...prev, ...respItems]);
                setProgress(Math.round(((i + 1) / chunks.length) * 100));
            }
            Swal.fire({
                title: 'Carga de carpeta',
                text: 'Se subieron los archivos de la carpeta exitosamente.',
                icon: 'success',
                iconColor: '#1d1d1d',
                confirmButtonColor: '#1d1d1d',
                allowOutsideClick: false,
                customClass: 'swal-wide-class',
            });
        } catch (error) {
            console.error('Bulk upload failed', error);
            Swal.fire({
                title: 'Error carga de carpeta',
                text: MasterMessage.commercialCrm.uploadFolder.messageError,
                icon: 'error',
                iconColor: '#1d1d1d',
                confirmButtonColor: '#1d1d1d',
                customClass: 'swal-wide-class',
            });
        } finally {
            setUploading(false);
        }
    };

    const handleSave = () => {
        setShowCsvViewer(false);
        if (files.length > 0 && Boolean(template)) {
            setLoading(true);
            setTemplate(undefined);
            setFiles([]);
            asyncPostLoadBaseGupshup(files[0], template || '')
                .then((body: any) => {
                    if (body.items !== undefined) {
                        setLoading(false);
                        Swal.fire({
                            title: 'Carga Exitosa',
                            text: `Se cargaron ${body.items.total} registros exitosamente.`,
                            icon: 'success',
                            iconColor: '#1d1d1d',
                            confirmButtonColor: '#1d1d1d',
                            allowOutsideClick: false,
                            customClass: 'swal-wide-class',
                        });
                    } else {
                        Swal.fire({
                            title: 'Error Carga',
                            text: messageError,
                            icon: 'error',
                            iconColor: '#1d1d1d',
                            confirmButtonColor: '#1d1d1d',
                            customClass: 'swal-wide-class',
                        });
                    }
                })
                .finally(() => setLoading(false));
        } else {
            SwalCustomClass.fire({
                text: 'Debe seleccionar un archivo y una lista',
                icon: 'error',
                iconColor: '#1d1d1d',
            });
        }
    };

    useEffect(() => {
        (async () => {
            const { items } = await asyncGetTemplates();
            setTemplates(
                items.map((item) => ({ id: item.id, name: item.description }))
            );
        })();
    }, []);

    return (
        <>
            {loading ? (
                <ModalLoading
                    open
                    title={titleModalLoadingWhatsappCustom}
                    message={messageModalLoading}
                />
            ) : (
                <>
                    <Stack spacing={3}>
                        <GupshupCsvHelpAccordionCustom />
                        <ListView
                            title={titleListWhatsappCustom}
                            titleButtonAdd={'Cargar'}
                            onClickAdd={() => setShowCsvViewer(true)}
                            startIconButtonAdd={
                                <FileUploadIcon color="secondary" />
                            }
                            disableBack
                            disableDataGrid
                            disabledAdd={!Boolean(template) || !files[0]}
                        />
                        <CSVRender
                            file={files[0]?.get('file')}
                            open={showCsvViewer}
                            onCloseCsvViewer={() => setShowCsvViewer(false)}
                            onAccept={handleSave}
                            requiredFields={requiredFieldsCustom}
                        />
                        <Grid item xs={12} mt={3}>
                            <AutocompleteField
                                data={templates}
                                label="Selecciona una plantilla"
                                name="template"
                                size="small"
                                required
                                value={template}
                                onChangeValue={(value) => setTemplate(value)}
                            />
                        </Grid>
                        <Grid
                            container
                            spacing={{ xs: 1.5, md: 1.5 }}
                            columnSpacing={{ xs: 1.5, sm: 1.5, md: 1.5 }}
                        >
                        <Grid item xs={12} mt={3} sx={{ minWidth: 300 }}>
                            <DropZoneField
                                name="File"
                                maxFiles={1}
                                accept=".csv"
                                onChange={(value) => setFiles(value)}
                            />
                        </Grid>
                        {/* Carga de carpeta a S3 (opcional) */}
                        <Grid item xs={12} mt={3}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="subtitle1" gutterBottom>
                                        {MasterMessage.commercialCrm.uploadFolder.title}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            mb: 2,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            startIcon={<FolderOpenIcon />}
                                            onClick={handlePickFolder}
                                        >
                                            Seleccionar carpeta
                                        </Button>
                                        <input
                                            ref={inputRef}
                                            type="file"
                                            multiple
                                            style={{ display: 'none' }}
                                            onChange={onFilesSelected}
                                        />
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<CloudUploadIcon />}
                                            onClick={startUpload}
                                            disabled={!bulkFiles.length || uploading}
                                        >
                                            Subir a S3
                                        </Button>
                                    </Box>
                                    <Box
                                        onDrop={onDrop}
                                        onDragOver={onDragOver}
                                        sx={{
                                            border: '2px dashed #888',
                                            borderRadius: 2,
                                            p: 3,
                                            textAlign: 'center',
                                            color: '#666',
                                        }}
                                    >
                                        Arrastra aquí la carpeta o usa "Seleccionar carpeta".
                                    </Box>
                                    {!!bulkFiles.length && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="body2">
                                                Archivos preparados: {bulkFiles.length} | Tamaño total: {totalSizeMB} MB
                                            </Typography>
                                        </Box>
                                    )}
                                    {uploading && (
                                        <Box sx={{ mt: 2 }}>
                                            <LinearProgress variant="determinate" value={progress} />
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                Progreso: {progress}%
                                            </Typography>
                                        </Box>
                                    )}
                                    {!!results.length && (
                                        <Box sx={{ mt: 3 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Resultados ({results.length})
                                            </Typography>
                                            <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                                                {results.map((r, idx) => (
                                                    <Box key={`${r.name}-${idx}`} sx={{ fontSize: 12 }}>
                                                        {r.name} → {r.url}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Stack>
            </>
        )}
    </>
    );
};

export default LoadBaseGupshupCustom;